import { LoggerService } from '../logger.service';

/**
 * Owns one FileSystemWritableFileStream. Exactly one of close()/abort() takes effect and both are
 * idempotent, so the writable is never left open on either path.
 */
export class StreamingSaveSession {
  private settled = false;
  private writtenBytes = 0;

  constructor(
    private handle: FileSystemFileHandle,
    private writable: FileSystemWritableFileStream,
    private loggerService: LoggerService,
  ) {}

  public get bytesWritten(): number {
    return this.writtenBytes;
  }

  public get isSettled(): boolean {
    return this.settled;
  }

  public async write(chunk: Uint8Array | Blob): Promise<void> {
    await this.writable.write(chunk);
    this.writtenBytes += chunk instanceof Blob ? chunk.size : chunk.byteLength;
  }

  /**
   * The completion signal — the swap file is atomically moved over the destination here, so
   * callers report a transfer complete only after this resolves.
   */
  public async close(): Promise<void> {
    if (this.settled) {
      return;
    }
    this.settled = true;
    try {
      await this.writable.close();
    } catch (e) {
      await this.discardDestination();
      throw e;
    }
  }

  /**
   * Cancellation and terminal failure share one policy. remove() deletes the 0-byte placeholder the
   * picker created, and must follow the abort or the writable's lock rejects it.
   */
  public async abort(reason?: unknown): Promise<void> {
    if (this.settled) {
      return;
    }
    this.settled = true;
    try {
      await this.writable.abort(reason);
    } catch (e) {
      this.loggerService.warn(`StreamingSaveSession: abort failed - ${e}`);
    }
    await this.discardDestination();
  }

  /** See StreamingSaveService.discardDestination: the picker truncated the target before any write. */
  private async discardDestination(): Promise<void> {
    try {
      await this.handle.remove?.();
    } catch (e) {
      this.loggerService.warn(`StreamingSaveSession: could not remove ${this.handle.name} - ${e}`);
    }
  }
}
